import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { withRouter } from 'react-router-dom';

import CollectionComposer from './CollectionComposer';
import TemplateImageTile from '../../components/catalog/TemplateImageTile';
import {
  createCollection,
  getAllCollections,
  subscribeToCollectionChanges,
  updateCollection,
} from '../../modules/collectionStorage';
import { aiApi } from '../../modules/api';
import { getTags, subscribeToTagChanges } from '../../modules/tagStorage';
import { getHistoryTagMap } from '../../modules/historyStorage';
import { HISTORY_ENTRY_REMOVED_EVENT } from '../../constants/history';

import { useCatalogData } from './CatalogDataContext';
import { fallbackImage } from './catalogData';
import './catalog.css';

const GENERATED_ARTWORK_STORAGE_KEY = 'asi:catalog-generated-artworks';
const FALLBACK_USER_ID = 'user-001';
const CURATED_BY_REGEX = /^curated by\s+/i;
const CREATED_BY_REGEX = /^created by\s+/i;
const SEARCH_STOP_WORDS = new Set([
  'and',
  'are',
  'can',
  'for',
  'from',
  'into',
  'the',
  'this',
  'that',
  'with',
  'your',
]);
const SMART_KEYWORD_GROUPS = [
  ['road', 'street', 'avenue', 'boulevard', 'path', 'lane', 'pavement', 'alley'],
  ['tree', 'forest', 'woods', 'woodland', 'canopy', 'leaf', 'leaves', 'flora'],
  ['lady', 'woman', 'female', 'portrait', 'model', 'fashion', 'person'],
  ['blue', 'cyan', 'teal', 'violet', 'indigo'],
  ['hood', 'hoodie', 'cloak', 'headwear', 'veil'],
  ['car', 'vehicle', 'transport', 'automotive'],
  ['building', 'architecture', 'tower', 'city', 'skyline'],
  ['food', 'ramen', 'noodle', 'sundae', 'coconut', 'candy'],
];
const DERIVED_KEYWORD_RULES = [
  { label: 'Road', terms: ['street', 'avenue', 'boulevard', 'path', 'lane', 'pavement', 'alley'] },
  { label: 'Tree', terms: ['forest', 'woods', 'woodland', 'canopy', 'leaf', 'leaves', 'flora'] },
  { label: 'Lady', terms: ['woman', 'female', 'portrait', 'model', 'fashion', 'person'] },
  { label: 'Blue', terms: ['blue', 'cyan', 'teal', 'violet', 'indigo'] },
  { label: 'Hood', terms: ['hood', 'hoodie', 'cloak', 'headwear', 'veil'] },
];

const normalizeSearchValue = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const isCharacterSwapTemplate = (item) => {
  if (!item) {
    return false;
  }

  const workflowFields = [
    item.taskType,
    item.type,
    item.mode,
    item.generationMode,
    item.workflow,
  ].map(normalizeSearchValue);
  if (workflowFields.some((value) => value === 'character swap')) {
    return true;
  }

  const title = normalizeSearchValue(item.title);
  const prompt = normalizeSearchValue(item.prompt);
  return title.includes('character swap') || prompt === 'character swap';
};

const tokenizeSearchValue = (value) =>
  normalizeSearchValue(value)
    .split(/\s+/)
    .filter((token) => token.length > 2 && !SEARCH_STOP_WORDS.has(token));

const getExpandedSearchTerms = (token) => {
  const normalized = normalizeSearchValue(token);
  const group = SMART_KEYWORD_GROUPS.find((items) => items.includes(normalized));
  return group || [normalized];
};

const searchTextHasTerm = (searchText, term) => {
  const normalizedTerm = normalizeSearchValue(term);
  if (!searchText || !normalizedTerm) {
    return false;
  }

  return ` ${searchText} `.includes(` ${normalizedTerm} `);
};

const getCollectionSearchText = (collection) => {
  if (!collection) {
    return '';
  }

  const fields = [
    collection.title,
    collection.description,
    collection.curator,
    ...(Array.isArray(collection.tags) ? collection.tags : []),
  ];

  if (Array.isArray(collection.artworks)) {
    collection.artworks.forEach((artwork) => {
      if (!artwork) {
        return;
      }
      fields.push(
        artwork.title,
        artwork.prompt,
        ...(Array.isArray(artwork.tags) ? artwork.tags : []),
      );
    });
  }

  return normalizeSearchValue(fields.filter(Boolean).join(' '));
};

const collectionMatchesSmartKeyword = (collection, keyword) => {
  const searchText = getCollectionSearchText(collection);
  const normalizedKeyword = normalizeSearchValue(keyword);
  const tokens = tokenizeSearchValue(keyword);

  if (!searchText || (!normalizedKeyword && !tokens.length)) {
    return false;
  }

  if (normalizedKeyword && searchText.includes(normalizedKeyword)) {
    return true;
  }

  if (!tokens.length) {
    return false;
  }

  const matchedTerms = tokens.filter((token) =>
    getExpandedSearchTerms(token).some((term) => searchTextHasTerm(searchText, term)),
  );

  if (tokens.length === 1) {
    return matchedTerms.length === 1;
  }

  return matchedTerms.length >= Math.min(2, tokens.length);
};

const deriveSmartKeywords = (collection) => {
  const searchText = getCollectionSearchText(collection);
  if (!searchText) {
    return [];
  }

  const derived = new Set();
  DERIVED_KEYWORD_RULES.forEach((rule) => {
    if (rule.terms.some((term) => searchTextHasTerm(searchText, term))) {
      derived.add(rule.label);
    }
  });

  tokenizeSearchValue(searchText).forEach((token) => {
    if (token.length > 18 || /^\d+$/.test(token)) {
      return;
    }
    derived.add(token.charAt(0).toUpperCase() + token.slice(1));
  });

  return Array.from(derived);
};

const extractCuratorName = (label) => {
  if (!label || typeof label !== 'string') {
    return '';
  }
  const trimmed = label.trim();
  if (!trimmed) {
    return '';
  }
  if (CURATED_BY_REGEX.test(trimmed)) {
    return trimmed.replace(CURATED_BY_REGEX, '').trim();
  }
  if (CREATED_BY_REGEX.test(trimmed)) {
    return '';
  }
  return trimmed;
};

const formatCuratorLabel = (name) => {
  if (name && typeof name === 'string' && name.trim()) {
    return `Curated by ${name.trim()}`;
  }
  return 'Workspace collection';
};

const enrichCollectionCurator = (collection) => {
  if (!collection || typeof collection !== 'object') {
    return collection;
  }
  const rawCurator =
    (collection.curator && typeof collection.curator === 'string' && collection.curator.trim()) ||
    '';
  const curatorName = extractCuratorName(rawCurator);
  const curatorLabel =
    rawCurator && (CURATED_BY_REGEX.test(rawCurator) || CREATED_BY_REGEX.test(rawCurator))
      ? rawCurator
      : formatCuratorLabel(curatorName || rawCurator);

  return {
    ...collection,
    curator: curatorLabel,
    curatorName,
  };
};

const sanitizeImage = (event) => {
  event.currentTarget.src = fallbackImage;
};

const formatUpdatedLabel = (isoString) => {
  if (!isoString) {
    return 'Just now';
  }

  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return 'Just now';
    }

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (error) {
    return 'Just now';
  }
};

const toTitleCase = (label) =>
  String(label || '')
    .split(/\s+/)
    .map((word) => {
      if (!word) {
        return '';
      }
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ')
    .trim();

const getTagLabel = (tag) => {
  if (!tag) {
    return '';
  }

  if (typeof tag === 'string') {
    return tag.trim();
  }

  if (typeof tag === 'object' && typeof tag.name === 'string') {
    return tag.name.trim();
  }

  return '';
};

const normalizeTagLabel = (tag) => {
  const label = getTagLabel(tag);
  return label ? toTitleCase(label) : '';
};

const normalizeTagList = (tags) => {
  if (!Array.isArray(tags)) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  tags.forEach((tag) => {
    const label = normalizeTagLabel(tag);
    if (!label) {
      return;
    }

    const key = label.toLowerCase();
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    normalized.push(label);
  });

  return normalized;
};

const buildTagSearchText = (tags) => normalizeSearchValue(normalizeTagList(tags).join(' '));

const getPersistedUserId = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return FALLBACK_USER_ID;
  }

  try {
    const rawUser = window.localStorage.getItem('asi:user');
    if (!rawUser) {
      return FALLBACK_USER_ID;
    }

    const parsedUser = JSON.parse(rawUser);
    if (parsedUser && typeof parsedUser.id === 'string' && parsedUser.id.trim()) {
      return parsedUser.id.trim();
    }
  } catch (error) {
    // Fall back to the demo user id when persisted auth cannot be parsed.
  }

  return FALLBACK_USER_ID;
};

const buildArtworks = (collectionId, total, baseUrls = []) => {
  if (!total) {
    total = Math.max(baseUrls.length, 6);
  }

  const uniqueBase = Array.from(new Set(baseUrls.filter(Boolean)));
  const trimmedBase = uniqueBase.slice(0, total);

  const normalizedBase = trimmedBase.map((url, index) => ({
    id: `${collectionId}-base-${index + 1}`,
    url,
    title: `${collectionId.replace(/-/g, ' ')} ${index + 1}`,
    prompt: `${collectionId.replace(/-/g, ' ')} ${index + 1} concept art, highly detailed`,
  }));

  const remaining = Math.max(total - normalizedBase.length, 0);

  const generated = Array.from({ length: remaining }, (_, index) => ({
    id: `${collectionId}-generated-${index + 1}`,
    url: `https://picsum.photos/seed/${encodeURIComponent(
      `${collectionId}-${normalizedBase.length + index + 1}`,
    )}/720/480`,
    title: `${collectionId.replace(/-/g, ' ')} ${normalizedBase.length + index + 1}`,
    prompt: `${collectionId.replace(/-/g, ' ')} ${normalizedBase.length + index + 1} artwork, cinematic lighting`,
  }));

  return [...normalizedBase, ...generated];
};

const getArtworkKey = (collectionId, artworkId) => `${collectionId}:${artworkId}`;

const normalizeFeaturedCollection = (collection) => {
  if (!collection) {
    return null;
  }

  const baseImages = [
    collection.cover,
    ...(Array.isArray(collection.previews) ? collection.previews : []),
  ].filter(Boolean);

  const desiredTotal =
    (collection.stats && Number(collection.stats.artworks)) ||
    (collection.artworks ? collection.artworks.length : 0) ||
    baseImages.length ||
    6;

  const baseArtworks =
    Array.isArray(collection.artworks) && collection.artworks.length > 0
      ? collection.artworks
      : buildArtworks(collection.id, desiredTotal, baseImages);

  const artworks = baseArtworks.filter((artwork) => !isCharacterSwapTemplate(artwork)).map((artwork, index) => {
    if (!artwork) {
      return null;
    }

    const safeId = artwork.id || `${collection.id}-artwork-${index + 1}`;
    const safeTitle = artwork.title || `${collection.title} artwork ${index + 1}`;
    const safePrompt = artwork.prompt || `${collection.title} — ${safeTitle}, highly detailed concept art`;
    const safeUrl = artwork.url || artwork.src || fallbackImage;
    const normalizedArtworkTags = normalizeTagList(artwork.tags);

    return {
      ...artwork,
      id: safeId,
      title: safeTitle,
      prompt: safePrompt,
      url: safeUrl,
      tags: normalizedArtworkTags,
    };
  }).filter(Boolean);

  const cover = collection.cover || (artworks[0] ? artworks[0].url : fallbackImage);
  const previews =
    Array.isArray(collection.previews) && collection.previews.length > 0
      ? collection.previews
      : artworks.slice(1, 3).map((artwork) => artwork.url);

  const stats = {
    artworks: artworks.length,
    updated: (collection.stats && collection.stats.updated) || 'Just now',
  };

  const rawCurator =
    (collection.curator && typeof collection.curator === 'string' && collection.curator.trim()) ||
    '';
  const curatorName = extractCuratorName(rawCurator);
  const curatorLabel =
    rawCurator && (CURATED_BY_REGEX.test(rawCurator) || CREATED_BY_REGEX.test(rawCurator))
      ? rawCurator
      : formatCuratorLabel(curatorName || rawCurator);

  return {
    ...collection,
    curator: curatorLabel,
    curatorName,
    cover,
    previews,
    artworks,
    stats,
    shouldGenerate: Boolean(collection.shouldGenerate),
    visibility: 'public',
  };
};

const mapStoredCollectionToCatalogEntry = (collection) => {
  if (!collection) {
    return null;
  }

  const artworks = Array.isArray(collection.artworks) ? collection.artworks : [];
  const normalizedArtworks = artworks.filter((artwork) => !isCharacterSwapTemplate(artwork)).map((artwork, index) => {
    if (!artwork) {
      return null;
    }
    const id = artwork.id || `${collection.id}-artwork-${index + 1}`;
    const title = artwork.title || `${collection.title} artwork ${index + 1}`;
    const artworkTags = normalizeTagList(artwork.tags);
    return {
      ...artwork,
      id,
      title,
      prompt: artwork.prompt || title,
      url: artwork.url || artwork.src || fallbackImage,
      tags: artworkTags,
    };
  }).filter(Boolean);

  const cover =
    (collection.coverImage && collection.coverImage.url) ||
    (normalizedArtworks[0] && normalizedArtworks[0].url) ||
    fallbackImage;

  const rawCurator =
    (collection.curator && typeof collection.curator === 'string' && collection.curator.trim()) ||
    '';
  const curatorName = extractCuratorName(rawCurator);
  const curatorLabel =
    rawCurator && (CURATED_BY_REGEX.test(rawCurator) || CREATED_BY_REGEX.test(rawCurator))
      ? rawCurator
      : formatCuratorLabel(curatorName || rawCurator);
  const normalizedTitle = toTitleCase(collection.title || '');
  const normalizedTags = normalizeTagList(collection.tags);

  return {
    id: collection.id,
    title: normalizedTitle,
    description: collection.description,
    curator: curatorLabel,
    curatorName,
    tags: normalizedTags,
    cover,
    previews: normalizedArtworks.slice(1, 3).map((artwork) => artwork.url),
    artworks: normalizedArtworks,
    stats: {
      artworks: normalizedArtworks.length,
      updated: formatUpdatedLabel(collection.updatedAt || collection.createdAt),
    },
    ownerId: collection.ownerId,
    createdAt: collection.createdAt,
    updatedAt: collection.updatedAt,
    shouldGenerate: false,
    visibility: collection.visibility === 'public' ? 'public' : 'private',
  };
};

const mapCollectionsToTemplates = (collections = [], generatedArtworks = {}) =>
  (Array.isArray(collections) ? collections : []).flatMap((collection) => {
    if (!collection || !Array.isArray(collection.artworks)) {
      return [];
    }

    return collection.artworks.map((artwork, index) => {
      if (!artwork || isCharacterSwapTemplate(artwork)) {
        return null;
      }
      const artworkId = artwork.id || `${collection.id}-artwork-${index + 1}`;
      const key = getArtworkKey(collection.id, artworkId);
      const imageUrl = generatedArtworks[key] || artwork.url || artwork.src || fallbackImage;
      return {
        id: `artwork:${collection.id}:${artworkId}`,
        templateId: `artwork:${collection.id}:${artworkId}`,
        source: 'catalog',
        sourceCollectionId: collection.id,
        sourceArtworkId: artworkId,
        sourceCollectionTitle: collection.title || '',
        title: artwork.title || `${collection.title} template ${index + 1}`,
        imageUrl,
        url: imageUrl,
        prompt: artwork.prompt || artwork.title || collection.description || '',
        tags: normalizeTagList([
          ...(Array.isArray(collection.tags) ? collection.tags : []),
          ...(Array.isArray(artwork.tags) ? artwork.tags : []),
        ]),
        visibility: collection.visibility === 'public' ? 'public' : 'private',
        isFeatured: Boolean(collection.isFeatured),
        featuredOrder: Number.isFinite(collection.featuredOrder) ? collection.featuredOrder : 0,
        ownerId: collection.ownerId || null,
        updatedAt: collection.updatedAt || null,
        createdAt: collection.createdAt || null,
      };
    }).filter(Boolean);
  });

const mapHistoryArtworkToTemplate = (artwork, index = 0) => {
  if (!artwork || !artwork.url) {
    return null;
  }

  const isCharacterSwap = isCharacterSwapTemplate(artwork);

  return {
    id: `history-template:${artwork.id}`,
    templateId: `history-template:${artwork.id}`,
    source: 'history',
    sourceHistoryId: artwork.rawId || artwork.id,
    title: artwork.title || (isCharacterSwap ? `Character swap ${index + 1}` : `My template ${index + 1}`),
    imageUrl: artwork.url,
    url: artwork.url,
    prompt: artwork.prompt || artwork.title || '',
    tags: normalizeTagList(artwork.tags),
    visibility: 'private',
    taskType: isCharacterSwap ? 'character_swap' : (artwork.taskType || 'generate'),
    sourceImages: Array.isArray(artwork.sourceImages) ? artwork.sourceImages : [],
    matchReason: null,
    createdAt: artwork.createdAt || null,
    updatedAt: artwork.createdAt || null,
  };
};

const getTemplateKind = (template) =>
  template && isCharacterSwapTemplate(template) ? 'character_swap' : 'image_gen';

const knownTemplateImageSearchText = [
  {
    imageId: 'photo-1500530855697-b586d89ba3ee',
    text: 'desert road highway mountain mountains red rock canyon rocks valley landscape scenic southwest',
  },
];

const getKnownTemplateImageSearchText = (template) => {
  const imageUrl = String((template && (template.imageUrl || template.url)) || '');
  if (!imageUrl) {
    return '';
  }
  const match = knownTemplateImageSearchText.find((entry) => imageUrl.includes(entry.imageId));
  return match ? match.text : '';
};

const getTemplateSearchText = (template) =>
  normalizeSearchValue([
    template && template.title,
    template && template.prompt,
    template && template.sourceCollectionTitle,
    getKnownTemplateImageSearchText(template),
    ...(template && Array.isArray(template.tags) ? template.tags : []),
  ].filter(Boolean).join(' '));

const templateMatchesSearch = (template, query) => {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) {
    return true;
  }

  const searchText = getTemplateSearchText(template);
  if (searchText.includes(normalizedQuery)) {
    return true;
  }

  const tokens = tokenizeSearchValue(query);
  if (!tokens.length) {
    return false;
  }

  const matchedTerms = tokens.filter((token) =>
    getExpandedSearchTerms(token).some((term) => searchTextHasTerm(searchText, term)),
  );

  return tokens.length === 1
    ? matchedTerms.length === 1
    : matchedTerms.length >= Math.min(2, tokens.length);
};

const Catalog = ({ history }) => {
  const {
    collections: catalogCollections = [],
    templates: catalogTemplates = [],
    searchPublicCollections,
  } = useCatalogData();
  const currentUserId = useMemo(() => getPersistedUserId(), []);

  const resultsRef = useRef(null);
  const [activeCollectionIndex, setActiveCollectionIndex] = useState(0);
  const [activeArtworkIndex, setActiveArtworkIndex] = useState(0);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [isCollectionPickerOpen, setIsCollectionPickerOpen] = useState(false);
  const [previewCollection, setPreviewCollection] = useState(null);
  const [storedCollections, setStoredCollections] = useState(() => {
    const all = getAllCollections();
    return Array.isArray(all) ? all.map(enrichCollectionCurator) : [];
  });
  const [tagLibrary, setTagLibrary] = useState(() => getTags());
  const [isTagViewerOpen, setIsTagViewerOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [recentCollectionIds, setRecentCollectionIds] = useState([]);
  const [editingCollection, setEditingCollection] = useState(null);
  const [historyArtworks, setHistoryArtworks] = useState([]);
  const [collectionVisibility, setCollectionVisibility] = useState('public');
  const [collectionSearchQuery, setCollectionSearchQuery] = useState('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [semanticTemplateMatches, setSemanticTemplateMatches] = useState([]);
  const [semanticCollectionMatches, setSemanticCollectionMatches] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const isCollectionPreviewOpen = Boolean(previewCollection);
  const isPublicView = collectionVisibility === 'public';
  const applyCollectionVisibility = useCallback(
    (nextVisibility, { closePicker = false } = {}) => {
      const normalizedVisibility = nextVisibility === 'public' ? 'public' : 'private';

      setCollectionVisibility((previous) =>
        previous === normalizedVisibility ? previous : normalizedVisibility,
      );
      setActiveCollectionIndex(0);
      setActiveArtworkIndex(0);
      setPreviewCollection(null);
      if (closePicker) {
        setIsCollectionPickerOpen(false);
      }
    },
    [
      setActiveArtworkIndex,
      setActiveCollectionIndex,
      setCollectionVisibility,
      setIsCollectionPickerOpen,
      setPreviewCollection,
    ],
  );
  const keepCollectionPickerOpenRef = useRef(false);
  const [generatedArtworks] = useState(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const raw = window.localStorage.getItem(GENERATED_ARTWORK_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  });
  const historyFetchInFlightRef = useRef(false);
  const semanticSearchRequestRef = useRef(0);

  useEffect(() => {
    setStoredCollections(() => {
      const all = getAllCollections();
      return Array.isArray(all) ? all.map(enrichCollectionCurator) : [];
    });

    const unsubscribe = subscribeToCollectionChanges((collectionsList) => {
      if (Array.isArray(collectionsList)) {
        setStoredCollections(collectionsList.map(enrichCollectionCurator));
      } else {
        setStoredCollections([]);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    setActiveCollectionIndex(0);
    setActiveArtworkIndex(0);
    setPreviewCollection(null);
    if (keepCollectionPickerOpenRef.current) {
      keepCollectionPickerOpenRef.current = false;
      return;
    }
    setIsCollectionPickerOpen(false);
  }, [isPublicView]);

  useEffect(() => {
    if (!isCollectionPickerOpen) {
      setCollectionSearchQuery('');
    }
  }, [isCollectionPickerOpen]);

  useEffect(() => {
    const unsubscribe = subscribeToTagChanges((tags) => {
      setTagLibrary(Array.isArray(tags) ? tags : []);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadHistoryArtworks = async () => {
      if (historyFetchInFlightRef.current) {
        return;
      }

      historyFetchInFlightRef.current = true;

      try {
        let offset = 0;
        let hasMore = true;
        const items = [];

        while (hasMore && offset < 500) {
          // The API caps a single history page at 100; load successive pages for Gallery.
          const { data } = await aiApi.fetchHistory({ limit: 100, offset });
          const responseData = data || {};
          const pageItems = Array.isArray(responseData.items) ? responseData.items : [];
          items.push(...pageItems);

          const pagination = responseData.pagination || {};
          hasMore = Boolean(pagination.hasMore) && pageItems.length > 0;
          offset = Number.isFinite(pagination.nextOffset)
            ? pagination.nextOffset
            : offset + pageItems.length;
        }

        if (isCancelled) {
          return;
        }

        const storedHistoryTags = getHistoryTagMap();
        const mapped = items
          .map((item, index) => {
            if (!item || !item.image || !item.image.url) {
              return null;
            }

            const rawId = item.id || item._id || item.createdAt || `history-${index}`;
            const safeId = `history-${String(rawId)}`;
            const promptText = (item.prompt && String(item.prompt)) || '';
            const itemTags = storedHistoryTags[safeId] || storedHistoryTags[String(rawId)] || item.tags || [];
            const isCharacterSwap = isCharacterSwapTemplate(item);

            return {
              id: safeId,
              rawId: item.id || item._id || null,
              url: item.image.url,
              title: isCharacterSwap ? 'Character Swap' : (promptText || `AI artwork ${index + 1}`),
              prompt: isCharacterSwap ? 'Character Swap' : promptText,
              tags: itemTags,
              taskType: item.taskType || item.type || item.mode || null,
              sourceImages: Array.isArray(item.sourceImages)
                ? item.sourceImages.filter((sourceImage) => sourceImage && sourceImage.url)
                : [],
              createdAt: item.createdAt || null,
            };
          })
          .filter(Boolean);

        setHistoryArtworks(mapped);
      } catch (error) {
        if (!isCancelled && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Failed to load AI history for collection composer', error);
        }
      } finally {
        historyFetchInFlightRef.current = false;
      }
    };

    loadHistoryArtworks();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleHistoryRemoval = (event) => {
      const detail = (event && event.detail) || {};
      const removedIds = [
        detail.id ? String(detail.id) : '',
        detail.safeId ? String(detail.safeId) : '',
      ].filter(Boolean);
      const removedUrls = detail.imageUrl ? [String(detail.imageUrl)] : [];

      if (removedIds.length === 0 && removedUrls.length === 0) {
        return;
      }

      setHistoryArtworks((previous) => {
        if (!Array.isArray(previous) || previous.length === 0) {
          return previous;
        }

        const idSet = new Set(removedIds);
        const urlSet = new Set(removedUrls);

        const next = previous.filter((artwork) => {
          if (!artwork) {
            return false;
          }
          const artworkId = artwork.id ? String(artwork.id) : '';
          const normalizedId = artworkId.startsWith('history-')
            ? artworkId
            : `history-${artworkId}`;
          const matchesId = artworkId && (idSet.has(artworkId) || idSet.has(normalizedId));
          const matchesUrl = artwork.url && urlSet.has(String(artwork.url));
          return !matchesId && !matchesUrl;
        });

        if (next.length === previous.length) {
          return previous;
        }

        return next;
      });
    };

    window.addEventListener(HISTORY_ENTRY_REMOVED_EVENT, handleHistoryRemoval);
    return () => {
      window.removeEventListener(HISTORY_ENTRY_REMOVED_EVENT, handleHistoryRemoval);
    };
  }, []);

  const normalizedFeaturedCollections = useMemo(
    () =>
      catalogCollections
        .map(normalizeFeaturedCollection)
        .filter(Boolean),
    [catalogCollections],
  );

  const normalizedPrivateCollections = useMemo(
    () =>
      storedCollections
        .filter((collection) => {
          if (!collection) {
            return false;
          }
          const visibility =
            typeof collection.visibility === 'string'
              ? collection.visibility.trim().toLowerCase()
              : 'private';
          if (visibility === 'public') {
            return false;
          }
          return collection.ownerId === currentUserId;
        })
        .map(mapStoredCollectionToCatalogEntry)
        .filter(Boolean),
    [currentUserId, storedCollections],
  );

  const normalizedStoredPublicCollections = useMemo(
    () =>
      storedCollections
        .filter((collection) => {
          if (!collection) {
            return false;
          }
          const visibility =
            typeof collection.visibility === 'string'
              ? collection.visibility.trim().toLowerCase()
              : 'private';
          return visibility === 'public';
        })
        .map(mapStoredCollectionToCatalogEntry)
        .filter(Boolean),
    [storedCollections],
  );

  const normalizedPublicCollections = useMemo(
    () => [...normalizedStoredPublicCollections, ...normalizedFeaturedCollections],
    [normalizedFeaturedCollections, normalizedStoredPublicCollections],
  );

  const publicTemplateItems = useMemo(() => {
    const apiTemplates = (Array.isArray(catalogTemplates) ? catalogTemplates : [])
      .filter((template) => template && template.imageUrl)
      .map((template) => ({
        ...template,
        url: template.url || template.imageUrl,
        visibility: 'public',
      }));
    const localTemplates = mapCollectionsToTemplates(normalizedStoredPublicCollections, generatedArtworks);
    const unique = new Map();

    [...localTemplates, ...apiTemplates].forEach((template) => {
      if (
        !template
        || !template.templateId
        || !template.imageUrl
        || isCharacterSwapTemplate(template)
      ) {
        return;
      }
      if (!unique.has(template.templateId)) {
        unique.set(template.templateId, template);
      }
    });

    return Array.from(unique.values());
  }, [catalogTemplates, generatedArtworks, normalizedStoredPublicCollections]);

  const privateTemplateItems = useMemo(() => {
    const historyTemplates = historyArtworks
      .map(mapHistoryArtworkToTemplate)
      .filter(Boolean);
    const collectionTemplates = mapCollectionsToTemplates(normalizedPrivateCollections, generatedArtworks);
    const unique = new Map();

    [...historyTemplates, ...collectionTemplates].forEach((template) => {
      if (
        !template
        || !template.templateId
        || !template.imageUrl
        || isCharacterSwapTemplate(template)
      ) {
        return;
      }
      if (!unique.has(template.templateId)) {
        unique.set(template.templateId, template);
      }
    });

    return Array.from(unique.values());
  }, [generatedArtworks, historyArtworks, normalizedPrivateCollections]);

  const combinedTemplateItems = useMemo(() => {
    const unique = new Map();
    [...publicTemplateItems, ...privateTemplateItems].forEach((template) => {
      if (!template || !template.templateId || !template.imageUrl) {
        return;
      }
      if (!unique.has(template.templateId)) {
        unique.set(template.templateId, template);
      }
    });
    return Array.from(unique.values());
  }, [privateTemplateItems, publicTemplateItems]);

  const featuredTemplateItems = useMemo(() => {
    const featured = publicTemplateItems
      .filter((template) => template && template.isFeatured)
      .sort((a, b) => {
        const orderDiff = (a.featuredOrder || 0) - (b.featuredOrder || 0);
        if (orderDiff !== 0) {
          return orderDiff;
        }
        return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
          sensitivity: 'base',
        });
      });
    return (featured.length > 0 ? featured : publicTemplateItems).slice(0, 36);
  }, [publicTemplateItems]);

  const heroTemplateItems = useMemo(() => {
    const sourceTemplates = featuredTemplateItems.length > 0
      ? featuredTemplateItems
      : publicTemplateItems;

    return sourceTemplates
      .map((template) => ({ template, sortKey: Math.random() }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .slice(0, 3)
      .map(({ template }) => template);
  }, [featuredTemplateItems, publicTemplateItems]);

  const visibleTemplateItems = useMemo(() => {
    const query = templateSearchQuery.trim();
    const hasFilter = Boolean(query || activeTagFilter);
    if (!hasFilter) {
      return featuredTemplateItems;
    }

    const semanticItems =
      query && semanticTemplateMatches.length > 0
        ? semanticTemplateMatches.filter((template) => !isCharacterSwapTemplate(template))
        : [];
    const semanticIds = new Set(semanticItems.map((template) => template.templateId || template.id));
    const localItems = combinedTemplateItems.filter((template) => {
      if (semanticIds.has(template.templateId || template.id)) {
        return false;
      }
      if (activeTagFilter) {
        const tags = normalizeTagList(template.tags);
        const normalizedFilter = activeTagFilter.trim().toLowerCase();
        const hasTag = tags.some((tag) => tag.toLowerCase() === normalizedFilter);
        if (!hasTag && !templateMatchesSearch(template, activeTagFilter)) {
          return false;
        }
      }
      return templateMatchesSearch(template, query);
    });

    return [...semanticItems, ...localItems];
  }, [
    activeTagFilter,
    combinedTemplateItems,
    featuredTemplateItems,
    semanticTemplateMatches,
    templateSearchQuery,
  ]);

  const combinedCollections = useMemo(
    () => (isPublicView ? normalizedPublicCollections : normalizedPrivateCollections),
    [isPublicView, normalizedPrivateCollections, normalizedPublicCollections],
  );

  const orderedCollections = useMemo(() => {
    const getPriority = (collection, index) => {
      const timestamps = [];
      if (collection.updatedAt) {
        const updated = new Date(collection.updatedAt).getTime();
        if (!Number.isNaN(updated)) {
          timestamps.push(updated);
        }
      }
      if (collection.createdAt) {
        const created = new Date(collection.createdAt).getTime();
        if (!Number.isNaN(created)) {
          timestamps.push(created);
        }
      }
      if (collection.stats && collection.stats.updatedISO) {
        const statUpdated = new Date(collection.stats.updatedISO).getTime();
        if (!Number.isNaN(statUpdated)) {
          timestamps.push(statUpdated);
        }
      }
      if (timestamps.length > 0) {
        return Math.max(...timestamps);
      }
      return combinedCollections.length - index;
    };

    return combinedCollections
      .map((collection, index) => ({
        collection,
        priority: getPriority(collection, index),
      }))
      .sort((a, b) => b.priority - a.priority)
      .map(({ collection }) => collection);
  }, [combinedCollections]);

  const aggregatedTags = useMemo(() => {
    const tagMap = new Map();

    const recordTag = (label, context = {}) => {
      const normalizedLabel = normalizeTagLabel(label);
      if (!normalizedLabel) {
        return;
      }

      const key = normalizedLabel.toLowerCase();
      const existing =
        tagMap.get(key) ||
        {
          key,
          name: normalizedLabel,
          libraryTagId: null,
          collectionIds: new Set(),
          privateCollectionIds: new Set(),
          publicCollectionIds: new Set(),
        };

      if (context.libraryTag) {
        existing.libraryTagId = context.libraryTag.id;
        existing.name = normalizeTagLabel(context.libraryTag.name);
      } else if (!tagMap.has(key)) {
        existing.name = normalizedLabel;
      }

      if (context.collectionId) {
        existing.collectionIds.add(context.collectionId);
        if (context.visibility === 'public') {
          existing.publicCollectionIds.add(context.collectionId);
        } else if (context.visibility === 'private') {
          existing.privateCollectionIds.add(context.collectionId);
        }
      }

      tagMap.set(key, existing);
    };

    tagLibrary.forEach((tag) => recordTag(tag && tag.name, { libraryTag: tag }));

    normalizedPrivateCollections.forEach((collection) => {
      if (!collection) {
        return;
      }
      [
        ...(Array.isArray(collection.tags) ? collection.tags : []),
        ...deriveSmartKeywords(collection),
      ].forEach((tag) => recordTag(tag, { collectionId: collection.id, visibility: 'private' }));
    });

    normalizedPublicCollections.forEach((collection) => {
      if (!collection) {
        return;
      }
      [
        ...(Array.isArray(collection.tags) ? collection.tags : []),
        ...deriveSmartKeywords(collection),
      ].forEach((tag) => recordTag(tag, { collectionId: collection.id, visibility: 'public' }));
    });

    return Array.from(tagMap.values())
      .map((entry) => ({
        key: entry.libraryTagId || entry.key,
        tagKey: entry.key,
        name: entry.name,
        libraryTagId: entry.libraryTagId,
        privateCount: entry.privateCollectionIds.size,
        publicCount: entry.publicCollectionIds.size,
        totalCount: entry.privateCollectionIds.size + entry.publicCollectionIds.size,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [
    normalizedPrivateCollections,
    normalizedPublicCollections,
    tagLibrary,
  ]);

  const tagSearchResults = useMemo(() => {
    if (!aggregatedTags.length) {
      return [];
    }

    const query = tagSearchQuery.trim().toLowerCase();
    const queryTokens = tokenizeSearchValue(query);
    const limit = 10;

    const compareByPopularity = (a, b) => {
      if (b.privateCount !== a.privateCount) {
        return b.privateCount - a.privateCount;
      }
      if (b.totalCount !== a.totalCount) {
        return b.totalCount - a.totalCount;
      }
      if (b.publicCount !== a.publicCount) {
        return b.publicCount - a.publicCount;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    };

    if (!query) {
      return aggregatedTags.slice().sort(compareByPopularity).slice(0, limit);
    }

    const ranked = aggregatedTags
      .filter((tag) => {
        const normalizedName = normalizeSearchValue(tag.name);
        const tagNameTokens = tokenizeSearchValue(normalizedName);
        return normalizedName.includes(query)
          || collectionMatchesSmartKeyword({ title: tag.name }, query)
          || queryTokens.some((token) =>
            getExpandedSearchTerms(token).some((term) =>
              tagNameTokens.includes(term),
            ),
          );
      })
      .map((tag) => {
        const normalizedName = normalizeSearchValue(tag.name);
        const tagNameTokens = tokenizeSearchValue(normalizedName);
        const tokenMatchCount = queryTokens.filter((token) =>
          getExpandedSearchTerms(token).some((term) =>
            tagNameTokens.includes(term),
          ),
        ).length;
        let score = 1;
        if (normalizedName === query) {
          score = 5;
        } else if (normalizedName.startsWith(query)) {
          score = 4;
        } else if (normalizedName.includes(query)) {
          score = 3;
        } else if (tokenMatchCount) {
          score = 2 + tokenMatchCount;
        }
        return { tag, score };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return compareByPopularity(a.tag, b.tag);
      })
      .map((entry) => entry.tag);

    return ranked.slice(0, limit);
  }, [aggregatedTags, tagSearchQuery]);

  const composerArtworks = useMemo(() => {
    const unique = new Map();

    const registerArtwork = (artwork, priority) => {
      if (!artwork || !artwork.id || !artwork.url) {
        return;
      }

      const existing = unique.get(artwork.id);
      if (existing) {
        if (priority > existing.priority) {
          unique.set(artwork.id, { ...existing, ...artwork, priority });
        }
        return;
      }

      unique.set(artwork.id, { ...artwork, priority });
    };

    historyArtworks.forEach((artwork, index) => {
      registerArtwork(
        {
          id: artwork.id,
          url: artwork.url,
          title: artwork.title || `AI artwork ${index + 1}`,
          prompt: artwork.prompt,
          collectionId: null,
          tags: Array.isArray(artwork.tags) ? artwork.tags.slice() : [],
        },
        Number.MAX_SAFE_INTEGER - index,
      );
    });

    const sourceCollections = [...normalizedPrivateCollections, ...normalizedPublicCollections];
    const totalCollections = sourceCollections.length || 1;

    sourceCollections.forEach((collection, collectionIndex) => {
      if (!collection || !Array.isArray(collection.artworks)) {
        return;
      }

      const updatedTimestamp =
        (collection.updatedAt && new Date(collection.updatedAt).getTime()) ||
        (collection.createdAt && new Date(collection.createdAt).getTime());
      const fallbackTimestamp = (totalCollections - collectionIndex) * 1000;
      const collectionPriority = Number.isFinite(updatedTimestamp)
        ? updatedTimestamp
        : fallbackTimestamp;

      collection.artworks.forEach((artwork, artworkIndex) => {
        if (!artwork) {
          return;
        }

        const artworkId = artwork.id || `${collection.id}-artwork-${artworkIndex + 1}`;
        const key = getArtworkKey(collection.id, artworkId);
        const resolvedUrl = generatedArtworks[key] || artwork.url || artwork.src || fallbackImage;

        registerArtwork(
          {
            id: artworkId,
            url: resolvedUrl,
            title: artwork.title || `${collection.title} artwork ${artworkIndex + 1}`,
            prompt: artwork.prompt,
            collectionId: collection.id,
            tags: normalizeTagList(artwork.tags),
          },
          collectionPriority - artworkIndex,
        );
      });
    });

    return Array.from(unique.values())
      .sort((a, b) => b.priority - a.priority)
      .map(({ priority, ...rest }) => rest);
  }, [generatedArtworks, historyArtworks, normalizedPrivateCollections, normalizedPublicCollections]);

  const filteredCollections = useMemo(() => {
    if (!activeTagFilter) {
      return orderedCollections;
    }

    const normalizedFilter = activeTagFilter.trim().toLowerCase();
    return orderedCollections.filter((collection) => {
      if (!collection || !Array.isArray(collection.tags)) {
        return false;
      }
      const exactTagMatch = normalizeTagList(collection.tags).some(
        (tag) => tag.toLowerCase() === normalizedFilter,
      );
      return exactTagMatch || collectionMatchesSmartKeyword(collection, activeTagFilter);
    });
  }, [activeTagFilter, orderedCollections]);

  useEffect(() => {
    if (recentCollectionIds.length === 0) {
      return;
    }

    const validIds = new Set(filteredCollections.map((collection) => collection.id));

    setRecentCollectionIds((previous) => {
      const next = previous.filter((id) => validIds.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [filteredCollections, recentCollectionIds.length]);

  const activateCollection = useCallback(
    (index, { closePicker = false } = {}) => {
      if (typeof index !== 'number' || Number.isNaN(index) || index < 0) {
        return;
      }
      const maxIndex = filteredCollections.length - 1;
      if (filteredCollections.length > 0 && index > maxIndex) {
        return;
      }
      setActiveCollectionIndex(index);
      setActiveArtworkIndex(0);
      if (closePicker) {
        setIsCollectionPickerOpen(false);
      }
    },
    [filteredCollections.length],
  );

  useEffect(() => {
    if (filteredCollections.length === 0) {
      activateCollection(0);
      return;
    }

    if (activeCollectionIndex > filteredCollections.length - 1) {
      activateCollection(0);
    }
  }, [activateCollection, activeCollectionIndex, filteredCollections]);

  useEffect(() => {
    setActiveArtworkIndex(0);
  }, [activeCollectionIndex]);

  const activeCollection = filteredCollections[activeCollectionIndex] || null;

  const ensureResultsInView = useCallback(() => {
    if (!resultsRef.current) {
      return;
    }

    resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (typeof resultsRef.current.focus === 'function') {
      resultsRef.current.focus({ preventScroll: true });
    }
  }, []);

  const handleToggleCollectionVisibilityInPicker = useCallback(() => {
    keepCollectionPickerOpenRef.current = true;
    applyCollectionVisibility(isPublicView ? 'private' : 'public');
  }, [applyCollectionVisibility, isPublicView]);

  useEffect(() => {
    if (!activeCollection || !activeCollection.id) {
      return;
    }

    setRecentCollectionIds((previous) => {
      if (previous[0] === activeCollection.id) {
        return previous;
      }

      const withoutCurrent = previous.filter((id) => id !== activeCollection.id);
      return [activeCollection.id, ...withoutCurrent];
    });
  }, [activeCollection]);

  const activeCollectionArtworks = useMemo(() => {
    if (!activeCollection || !Array.isArray(activeCollection.artworks)) {
      return [];
    }

    return activeCollection.artworks.map((artwork, index) => {
      if (!artwork) {
        return null;
      }
      const id = artwork.id || `${activeCollection.id}-artwork-${index + 1}`;
      const key = getArtworkKey(activeCollection.id, id);
      const generatedUrl = generatedArtworks[key];
      const title = artwork.title || `${activeCollection.title} artwork ${index + 1}`;
      const artworkTags = normalizeTagList(artwork.tags);
      return {
        ...artwork,
        id,
        title,
        prompt: artwork.prompt || title,
        url: generatedUrl || artwork.url || artwork.src || fallbackImage,
        tags: artworkTags,
      };
    }).filter(Boolean);
  }, [activeCollection, generatedArtworks]);

  const totalArtworks = activeCollectionArtworks.length;

  useEffect(() => {
    if (totalArtworks === 0) {
      setActiveArtworkIndex(0);
      return;
    }

    if (activeArtworkIndex > totalArtworks - 1) {
      setActiveArtworkIndex(totalArtworks - 1);
    }
  }, [activeArtworkIndex, totalArtworks]);

  const computeCollectionMatches = useCallback(
    (query) => {
      const normalizedQuery = query.trim().toLowerCase();
      const limit = 6;

      const rankCollections = (collections, priority) =>
        collections
          .map((collection) => {
            if (!collection) {
              return null;
            }
            const title = (collection.title || '').toLowerCase();
            const curator = (collection.curator || '').toLowerCase();
            const description = (collection.description || '').toLowerCase();
            const tags = buildTagSearchText(collection.tags);
            const artworks = Array.isArray(collection.artworks)
              ? collection.artworks
                .map((artwork) => [
                  artwork && artwork.title,
                  artwork && artwork.prompt,
                  artwork && buildTagSearchText(artwork.tags),
                ].filter(Boolean).join(' '))
                .join(' ')
              : '';

            const fields = [title, curator, description, tags, normalizeSearchValue(artworks)];
            if (!normalizedQuery) {
              return { collection, score: priority };
            }

            const matches = fields.filter((value) => value && value.includes(normalizedQuery));
            const smartMatch = collectionMatchesSmartKeyword(collection, normalizedQuery);
            if (matches.length === 0 && !smartMatch) {
              return null;
            }

            const score =
              matches.reduce((total, value) => {
                if (value === normalizedQuery) {
                  return total + 3;
                }
                if (value.startsWith(normalizedQuery)) {
                  return total + 2;
                }
                return total + 1;
              }, smartMatch ? 1 : 0) + priority;

            return { collection, score };
          })
          .filter(Boolean);

      const privateCandidates = rankCollections(normalizedPrivateCollections, 5);
      const publicCandidates = rankCollections(normalizedPublicCollections, 1);

      const combinedEntries = [...privateCandidates, ...publicCandidates].sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.collection.title.localeCompare(b.collection.title, undefined, {
          sensitivity: 'base',
        });
      });

      const seen = new Set();
      const capped = [];

      combinedEntries.some((entry) => {
        const collection = entry.collection;
        if (seen.has(collection.id)) {
          return false;
        }

        if (filteredCollections.length > 0) {
          const isPublic = normalizedPublicCollections.some((item) => item.id === collection.id);
          if (isPublic && normalizedQuery) {
            const title = (collection.title || '').toLowerCase();
            const curator = (collection.curator || '').toLowerCase();
            const description = (collection.description || '').toLowerCase();
            const tags = buildTagSearchText(collection.tags);
            const matchesQuery = [title, curator, description, tags].some(
              (value) => value && value.includes(normalizedQuery),
            ) || collectionMatchesSmartKeyword(collection, normalizedQuery);
            if (!matchesQuery) {
              return false;
            }
          }
        }

        capped.push(collection);
        seen.add(collection.id);
        return capped.length >= limit;
      });

      return capped;
    },
    [filteredCollections, normalizedPublicCollections, normalizedPrivateCollections],
  );

  const pickerCollections = useMemo(
    () => {
      const localMatches = computeCollectionMatches(collectionSearchQuery);
      const normalizedQuery = collectionSearchQuery.trim();
      if (!isPublicView || !normalizedQuery || semanticCollectionMatches.length === 0) {
        return localMatches;
      }

      const seen = new Set();
      const filteredById = new Map(
        filteredCollections.map((collection) => [collection.id, collection]),
      );
      return [...semanticCollectionMatches, ...localMatches].filter((collection) => {
        if (!collection || !collection.id || seen.has(collection.id)) {
          return false;
        }
        seen.add(collection.id);
        return true;
      }).map((collection) => filteredById.get(collection.id) || collection).slice(0, 8);
    },
    [
      collectionSearchQuery,
      computeCollectionMatches,
      filteredCollections,
      isPublicView,
      semanticCollectionMatches,
    ],
  );

  useEffect(() => {
    const query = templateSearchQuery.trim();
    if (query.length < 2) {
      setSemanticTemplateMatches([]);
      return undefined;
    }

    const requestId = semanticSearchRequestRef.current + 1;
    semanticSearchRequestRef.current = requestId;

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await searchPublicCollections(query);
        if (semanticSearchRequestRef.current !== requestId) {
          return;
        }
        const items = Array.isArray(result.items) && result.items.length > 0
          ? result.items
          : mapCollectionsToTemplates(result.collections || [], generatedArtworks);
        setSemanticTemplateMatches(
          result.enabled && Array.isArray(items)
            ? items.map((template) => ({
                ...template,
                matchReason: template.matchReason || 'Related semantic match',
              }))
            : [],
        );
      } catch (error) {
        if (semanticSearchRequestRef.current === requestId) {
          setSemanticTemplateMatches([]);
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Template semantic search failed', error);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    generatedArtworks,
    searchPublicCollections,
    templateSearchQuery,
  ]);

  useEffect(() => {
    const query = collectionSearchQuery.trim();
    if (!isCollectionPickerOpen || !isPublicView || query.length < 2) {
      setSemanticCollectionMatches([]);
      return undefined;
    }

    const requestId = semanticSearchRequestRef.current + 1;
    semanticSearchRequestRef.current = requestId;

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await searchPublicCollections(query);
        if (semanticSearchRequestRef.current !== requestId) {
          return;
        }
        setSemanticCollectionMatches(
          result.enabled && Array.isArray(result.collections) ? result.collections : [],
        );
      } catch (error) {
        if (semanticSearchRequestRef.current === requestId) {
          setSemanticCollectionMatches([]);
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Catalog semantic search failed', error);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    collectionSearchQuery,
    isCollectionPickerOpen,
    isPublicView,
    searchPublicCollections,
  ]);

  const handleCloseCollectionPreview = useCallback(() => {
    setPreviewCollection(null);
  }, []);

  const handleCloseCollectionPicker = useCallback(() => {
    setIsCollectionPickerOpen(false);
  }, []);

  useEffect(() => {
    if (!previewCollection) {
      return;
    }
    if (!filteredCollections.some((collection) => collection.id === previewCollection.id)) {
      setPreviewCollection(null);
    }
  }, [filteredCollections, previewCollection]);

  useEffect(() => {
    if (isCollectionPickerOpen && filteredCollections.length === 0) {
      setIsCollectionPickerOpen(false);
    }
  }, [filteredCollections.length, isCollectionPickerOpen]);

  useEffect(() => {
    if (!previewCollection || !activeCollection) {
      return;
    }
    if (previewCollection.id === activeCollection.id) {
      setPreviewCollection(activeCollection);
    }
  }, [activeCollection, previewCollection]);

  const handleCloseTagViewer = useCallback(() => {
    setIsTagViewerOpen(false);
    setTagSearchQuery('');
  }, [setIsTagViewerOpen, setTagSearchQuery]);

  const handleSelectTag = useCallback(
    (tagEntry) => {
      const tagName = typeof tagEntry === 'string' ? tagEntry : tagEntry && tagEntry.name;
      if (!tagName) {
        return;
      }

      if (
        !isPublicView
        && tagEntry
        && typeof tagEntry === 'object'
        && tagEntry.privateCount === 0
        && tagEntry.publicCount > 0
      ) {
        applyCollectionVisibility('public');
      }
      setActiveTagFilter(tagName);
      setActiveCollectionIndex(0);
      setActiveArtworkIndex(0);
      setIsCollectionPickerOpen(false);
      setIsTagViewerOpen(false);
      setTagSearchQuery('');
      ensureResultsInView();
      message.success(`Showing collections matching “${tagName}”.`);
    },
    [applyCollectionVisibility, ensureResultsInView, isPublicView, message, setTagSearchQuery],
  );

  const handleClearTagFilter = useCallback(() => {
    setActiveTagFilter(null);
    activateCollection(0);
    ensureResultsInView();
    message.info('Showing all collections.');
  }, [activateCollection, ensureResultsInView, message]);

  const handleUseTemplate = useCallback(
    (template, referenceMode) => {
      if (!template) {
        return;
      }
      const promptText = (template.prompt || template.title || '').trim();
      if (!promptText) {
        message.warning('That template does not have a reusable prompt yet.');
        return;
      }

      history.push('/ai', {
        prompt: promptText,
        focusTab: 'generate',
        referenceImageUrls: referenceMode && template.imageUrl ? [template.imageUrl] : [],
      });

      if (referenceMode === 'style') {
        message.success('Template loaded as a style reference.');
      } else {
        message.success('Template prompt loaded in the generator.');
      }
    },
    [history],
  );

  const handleOpenTemplateDetails = useCallback((template) => {
    if (!template) {
      return;
    }
    setSelectedTemplate(template);
  }, []);

  const handleCloseTemplateDetails = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  const handleOpenCollection = useCallback(
    (collection, event) => {
      if (!collection) {
        message.info('Collection details coming soon.');
        return;
      }
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      history.push(`/templates/collections/${collection.id}`, { collection });
    },
    [history],
  );

  const handleShareCollection = useCallback(async (collection) => {
    if (!collection) {
      return;
    }
    if (typeof window === 'undefined') {
      setShareFeedback({
        type: 'collection',
        id: collection.id,
        message: 'Sharing is only available in the browser.',
      });
      setTimeout(() => setShareFeedback(null), 3200);
      return;
    }

    const shareUrl = `${window.location.origin}/templates/collections/${collection.id}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: collection.title,
          text: collection.description,
          url: shareUrl,
        });
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setShareFeedback({
        type: 'collection',
        id: collection.id,
        message: 'Link copied to clipboard',
      });
      setTimeout(() => setShareFeedback(null), 3200);
    } catch (error) {
      setShareFeedback({
        type: 'collection',
        id: collection.id,
        message: 'Unable to share. Copy the link manually.',
      });
      setTimeout(() => setShareFeedback(null), 3200);
    }
  }, []);

  const handleComposerSubmit = useCallback(
    (payload) => {
      const rawCuratorInput = (payload.curator || '').trim();
      const curatorName = extractCuratorName(rawCuratorInput);
      const curatorLabel = formatCuratorLabel(curatorName);

      const normalizedTitle = toTitleCase(payload.title);
      const normalizedTags = normalizeTagList(payload.tags);

      const normalizedArtworks = Array.isArray(payload.artworks)
        ? payload.artworks.map((artwork, index) => {
            if (!artwork) {
              return null;
            }
            const normalizedArtworkTags = normalizeTagList(artwork.tags);
            return {
              ...artwork,
              title: artwork.title || `Artwork ${index + 1}`,
              tags: normalizedArtworkTags,
            };
          }).filter(Boolean)
        : [];

      const submission = {
        title: normalizedTitle,
        curator: curatorLabel,
        description: payload.description,
        tags: normalizedTags,
        artworks: normalizedArtworks,
        visibility: payload.visibility === 'public' ? 'public' : 'private',
      };

      try {
        if (payload.id) {
          const updated = updateCollection(payload.id, submission);

          if (!updated) {
            throw new Error('Unable to update collection.');
          }

          const normalizedUpdated = {
            ...updated,
            curator: curatorLabel,
            curatorName,
          };

          setStoredCollections((previous) => {
            if (!Array.isArray(previous) || previous.length === 0) {
              return [normalizedUpdated];
            }
            let found = false;
            const next = previous.map((collection) => {
              if (collection && collection.id === normalizedUpdated.id) {
                found = true;
                return { ...collection, ...normalizedUpdated };
              }
              return collection;
            });
            return found ? next : [normalizedUpdated, ...next];
          });
          setRecentCollectionIds((previous) => [
            normalizedUpdated.id,
            ...previous.filter((id) => id !== normalizedUpdated.id),
          ]);
          applyCollectionVisibility(submission.visibility);
          message.success('Collection updated.');
        } else {
          const saved = createCollection({
            ...submission,
            ownerId: currentUserId,
          });
          const normalizedSaved = {
            ...saved,
            curator: curatorLabel,
            curatorName,
          };
          setStoredCollections((previous) => {
            const next = Array.isArray(previous) ? previous.slice() : [];
            if (next.some((collection) => collection.id === normalizedSaved.id)) {
              return next;
            }
            next.unshift(normalizedSaved);
            return next;
          });
          setRecentCollectionIds((previous) => [
            normalizedSaved.id,
            ...previous.filter((id) => id !== normalizedSaved.id),
          ]);
          message.success('Collection saved to your catalog.');
          applyCollectionVisibility(submission.visibility);
          activateCollection(0);
        }

        setEditingCollection(null);
        setIsComposerOpen(false);
        setIsCollectionPickerOpen(false);
        ensureResultsInView();
      } catch (error) {
        const errorMessage = error && error.message ? error.message : 'Unable to save collection.';
        message.error(errorMessage);
      }
    },
    [activateCollection, applyCollectionVisibility, currentUserId, ensureResultsInView],
  );

  return (
    <section className="catalog">
      <section className="catalog-hero" aria-labelledby="catalog-hero-heading">
        <div className="catalog-hero__content">
          <span className="catalog-hero__eyebrow">Template library</span>
          <h1 id="catalog-hero-heading">Templates</h1>
          <p>
            Search reusable image templates by subject, scene, style, color, or prompt.
          </p>
          <div className="catalog-template-search catalog-template-search--hero">
            <label htmlFor="catalog-template-search">Search templates</label>
            <div className="catalog-template-search__control">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                id="catalog-template-search"
                type="search"
                value={templateSearchQuery}
                onChange={(event) => setTemplateSearchQuery(event.target.value)}
                placeholder='Try "road", "tree", "blue hood", or "fashion portrait"'
              />
              {templateSearchQuery && (
                <button
                  type="button"
                  onClick={() => setTemplateSearchQuery('')}
                  aria-label="Clear template search"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="catalog-hero__media" aria-label="Featured template previews">
          <div className="catalog-hero__tiles">
            {heroTemplateItems.map((template, index) => (
              <TemplateImageTile
                key={template.templateId || template.id || `hero-template-${index}`}
                className={`catalog-hero__tile catalog-hero__template-tile catalog-hero__tile--${index}`}
                imageUrl={template.imageUrl || template.url || fallbackImage}
                fallbackImageUrl={fallbackImage}
                title={template.title}
                interactive
                onDetails={() => handleOpenTemplateDetails(template)}
                onOpen={() => handleUseTemplate(template)}
                onReprompt={() => handleUseTemplate(template)}
                onStyleReference={() => handleUseTemplate(template, 'style')}
              />
            ))}
          </div>
        </div>
      </section>

      {activeTagFilter && (
        <div className="catalog__filter-status" role="status">
          <span>
            Showing templates matching <strong>{activeTagFilter}</strong>
          </span>
          <button type="button" onClick={handleClearTagFilter} className="catalog__filter-clear">
            Clear filter
          </button>
        </div>
      )}

      <section
        ref={resultsRef}
        className="catalog__section"
        aria-labelledby="catalog-results"
        tabIndex={-1}
      >
        <div className="catalog__section-header catalog__section-header--templates">
          <div className="catalog__section-title-row">
            <h2 id="catalog-results">
              {templateSearchQuery.trim() || activeTagFilter ? 'Template Search' : 'Featured Templates'}
            </h2>
          </div>
          <p>
            {templateSearchQuery.trim() || activeTagFilter
              ? 'Search covers featured public templates and your Gallery images together.'
              : 'Start with featured public templates, or search across public templates and your Gallery.'}
          </p>
        </div>

        {visibleTemplateItems.length > 0 ? (
          <div className="catalog-template-grid" aria-live="polite">
            {visibleTemplateItems.map((template, index) => (
              <TemplateImageTile
                key={template.templateId || template.id}
                className={`catalog-template-tile catalog-template-tile--${(index % 6) + 1}`}
                imageUrl={template.imageUrl || template.url || fallbackImage}
                fallbackImageUrl={fallbackImage}
                title={template.title}
                interactive
                detachOverlayFromColumn
                onDetails={() => handleOpenTemplateDetails(template)}
                onOpen={() => handleUseTemplate(template)}
                onReprompt={() => handleUseTemplate(template)}
                onStyleReference={() => handleUseTemplate(template, 'style')}
              />
            ))}
          </div>
        ) : (
          <p className="catalog-carousel__empty">
            No templates match this search yet. Try a broader subject, clear the filters, or open Gallery.
          </p>
        )}
      </section>

      {selectedTemplate && (
        <div
          className="catalog-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-detail-title"
        >
          <div
            className="catalog-modal__backdrop"
            role="presentation"
            onClick={handleCloseTemplateDetails}
          />
          <div className="catalog-modal__panel catalog-modal__panel--template-detail">
            <button
              type="button"
              className="catalog-modal__close"
              onClick={handleCloseTemplateDetails}
              aria-label="Close template details"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <div className="catalog-template-detail">
              <figure className="catalog-template-detail__media">
                <img
                  src={selectedTemplate.imageUrl || selectedTemplate.url || fallbackImage}
                  alt={selectedTemplate.title || 'Generated image'}
                  onError={sanitizeImage}
                />
              </figure>
              <div className="catalog-template-detail__body">
                <header className="catalog-template-detail__header">
                  <span className="catalog-template-detail__eyebrow">
                    {getTemplateKind(selectedTemplate) === 'character_swap'
                      ? 'Character Swap'
                      : 'Image Gen'}
                  </span>
                  <h2 id="template-detail-title">
                    {selectedTemplate.title || 'Generated image'}
                  </h2>
                </header>

                {getTemplateKind(selectedTemplate) === 'character_swap' ? (
                  <p className="catalog-template-detail__note">
                    Character Swap results are shown with minimal metadata and can be reused as
                    visual references.
                  </p>
                ) : (
                  <div className="catalog-template-detail__field">
                    <span>Prompt</span>
                    <p>{selectedTemplate.prompt || 'No prompt saved for this image.'}</p>
                  </div>
                )}

                <div className="catalog-template-detail__field">
                  <span>Tags</span>
                  {Array.isArray(selectedTemplate.tags) && selectedTemplate.tags.length > 0 ? (
                    <ul className="catalog-template-detail__tags">
                      {selectedTemplate.tags.map((tag) => (
                        <li key={`${selectedTemplate.templateId || selectedTemplate.id}-${tag}`}>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No tags saved yet.</p>
                  )}
                </div>

                <div className="catalog-template-detail__actions">
                  <button type="button" onClick={() => handleUseTemplate(selectedTemplate)}>
                    Recreate
                  </button>
                  <button type="button" onClick={() => handleUseTemplate(selectedTemplate)}>
                    Reprompt
                  </button>
                  <button type="button" onClick={() => handleUseTemplate(selectedTemplate, 'style')}>
                    Style Reference
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTagViewerOpen && (
        <div
          className="catalog-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tag-viewer-title"
        >
          <div
            className="catalog-modal__backdrop"
            role="presentation"
            onClick={handleCloseTagViewer}
          />
          <div className="catalog-modal__panel catalog-modal__panel--tags">
            <button
              type="button"
              className="catalog-modal__close"
              onClick={handleCloseTagViewer}
              aria-label="Close keyword viewer"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <header className="catalog-modal__header">
              <h2 id="tag-viewer-title">Browse Tags</h2>
              <p>Search template titles, prompts, and tags. Related terms such as road, tree, or portrait can match even when they were not manually tagged.</p>
            </header>
            <div className="catalog-tag-viewer">
              {aggregatedTags.length > 0 ? (
                <>
                  <div className="catalog-tag-viewer__search">
                    <label htmlFor="catalog-tag-viewer-search">Search keywords</label>
                    <input
                      id="catalog-tag-viewer-search"
                      type="search"
                      value={tagSearchQuery}
                      onChange={(event) => setTagSearchQuery(event.target.value)}
                      placeholder="Search keywords..."
                    />
                    <p className="catalog-tag-viewer__hint">
                      {tagSearchQuery
                        ? 'Top keyword and prompt matches appear below.'
                        : `Showing top ${Math.min(aggregatedTags.length, 10)} keywords.`}
                    </p>
                  </div>
                  {tagSearchResults.length > 0 ? (
                    <ul className="catalog-tag-viewer__list">
                      {tagSearchResults.map((tag) => {
                        const isActive =
                          activeTagFilter &&
                          activeTagFilter.trim().toLowerCase() === tag.name.trim().toLowerCase();

                        return (
                          <li
                            key={tag.key}
                            className={`catalog-tag-viewer__item${isActive ? ' is-active' : ''}`}
                          >
                            <button
                              type="button"
                              className="catalog-tag-viewer__select"
                              onClick={() => handleSelectTag(tag)}
                            >
                              <span className="catalog-tag-viewer__name">{tag.name}</span>
                              <span className="catalog-tag-viewer__badge" aria-label={`${tag.totalCount} templates`}>
                                {tag.totalCount}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="catalog-tag-viewer__empty">
                      No matching tag or prompt matches yet. Try a broader term.
                    </p>
                  )}
                </>
              ) : (
                <p className="catalog-tag-viewer__empty">
                  No tags yet. Add one to get started.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isCollectionPickerOpen && (
        <div
          className="catalog-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="collection-picker-title"
        >
          <div
            className="catalog-modal__backdrop"
            role="presentation"
            onClick={handleCloseCollectionPicker}
          />
          <div className="catalog-modal__panel catalog-modal__panel--collections">
            <button
              type="button"
              className="catalog-modal__close"
              onClick={handleCloseCollectionPicker}
              aria-label="Close collection picker"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <header className="catalog-modal__header">
              <h2 id="collection-picker-title">Browse Collections</h2>
              <p>
                Private collections are yours only. Public collections are platform templates shared with everyone.
              </p>
            </header>
            <div className="catalog-collection-picker">
              <div className="catalog-collection-picker__visibility">
                <span
                  id="catalog-collection-picker-visibility-label"
                  className="catalog-collection-picker__visibility-label"
                >
                  Search within
                </span>
                <div
                  className="catalog__collection-toggle catalog-collection-picker__toggle"
                  role="group"
                  aria-labelledby="catalog-collection-picker-visibility-label"
                >
                  <span
                    className={`catalog__collection-toggle-label${!isPublicView ? ' is-active' : ''}`}
                  >
                    My private
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPublicView}
                    className={`catalog__collection-toggle-switch${isPublicView ? ' is-public' : ''}`}
                    onClick={handleToggleCollectionVisibilityInPicker}
                  >
                    <span className="catalog__collection-toggle-thumb" aria-hidden="true" />
                    <span className="sr-only">
                      {isPublicView ? 'Switch to private collections' : 'Switch to public collections'}
                    </span>
                  </button>
                  <span
                    className={`catalog__collection-toggle-label${isPublicView ? ' is-active' : ''}`}
                  >
                    Platform public
                  </span>
                </div>
              </div>
              <div className="catalog-collection-picker__search">
                <label htmlFor="catalog-collection-search">Search collections</label>
                <input
                  id="catalog-collection-search"
                  type="search"
                  value={collectionSearchQuery}
                  onChange={(event) => setCollectionSearchQuery(event.target.value)}
                  placeholder="Search collections..."
                />
                <p className="catalog-collection-picker__hint">
                  Showing up to six matches from the selected visibility.
                </p>
              </div>
              {pickerCollections.length > 0 ? (
                <div className="catalog-modal__grid catalog-modal__grid--collections">
                  {pickerCollections.map((collection) => {
                    const collectionIndex = filteredCollections.findIndex(
                      (item) => item && collection && item.id === collection.id,
                    );
                    if (collectionIndex === -1) {
                      return null;
                    }
                    const firstArtwork =
                  Array.isArray(collection.artworks) && collection.artworks.length > 0
                    ? collection.artworks[0]
                    : null;
                const artworkId = firstArtwork && firstArtwork.id ? firstArtwork.id : `${collection.id}-artwork-1`;
                const previewKey = firstArtwork ? getArtworkKey(collection.id, artworkId) : null;
                const previewUrl =
                  (previewKey && generatedArtworks[previewKey]) ||
                  (firstArtwork && (firstArtwork.url || firstArtwork.src)) ||
                  (collection.previews && collection.previews[0]) ||
                  collection.cover ||
                  fallbackImage;
                const isActive = collectionIndex === activeCollectionIndex;

                return (
                  <button
                    key={collection.id}
                    type="button"
                    className={`catalog-composer__artwork-option catalog-modal__collection-option${
                      isActive ? ' is-active' : ''
                    }`}
                    aria-pressed={isActive}
                    onClick={() => {
                      activateCollection(collectionIndex, { closePicker: true });
                      ensureResultsInView();
                    }}
                  >
                    <img src={previewUrl} alt={`${collection.title} cover`} loading="lazy" />
                    <span className="catalog-composer__artwork-option-overlay">{collection.title}</span>
                    {isActive && (
                      <span className="catalog-composer__artwork-option-check" aria-hidden="true">
                        <i className="fa-solid fa-check" />
                      </span>
                    )}
                  </button>
                  );
                })}
                </div>
              ) : (
                <p className="catalog-collection-picker__empty">
                  No collections match this search yet. Try a different keyword.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isCollectionPreviewOpen && previewCollection && (
        <div
          className="catalog-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="collection-preview-title"
        >
          <div
            className="catalog-modal__backdrop"
            role="presentation"
            onClick={handleCloseCollectionPreview}
          />
          <div className="catalog-modal__panel catalog-modal__panel--preview">
            <button
              type="button"
              className="catalog-modal__close"
              onClick={handleCloseCollectionPreview}
              aria-label="Close collection preview"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <header className="catalog-modal__header">
              <h2 id="collection-preview-title">{previewCollection.title}</h2>
              {previewCollection.description && <p>{previewCollection.description}</p>}
            </header>
            <article className="catalog-card catalog-card--collection catalog-modal__collection-card">
              <div className="catalog-card__body">
                <div className="catalog-card__preamble">
                  {previewCollection.curator || formatCuratorLabel('')}
                </div>
                <div className="catalog-modal__collection-meta">
                  <dl className="catalog-carousel__meta">
                    <div>
                      <dt>Artworks</dt>
                      <dd>{Array.isArray(previewCollection.artworks) ? previewCollection.artworks.length : 0}</dd>
                    </div>
                    <div>
                      <dt>Updated</dt>
                      <dd>{previewCollection.stats && previewCollection.stats.updated}</dd>
                    </div>
                  </dl>
                </div>
                <div
                  className="catalog-card__gallery catalog-card__gallery--compact"
                  aria-label={`${Array.isArray(previewCollection.artworks) ? previewCollection.artworks.length : 0} artworks in ${previewCollection.title}`}
                >
                  {(previewCollection.artworks || []).slice(0, 6).map((artwork, index) => (
                    <img
                      key={artwork.id || `${previewCollection.id}-preview-${index}`}
                      src={artwork.url}
                      alt={`${previewCollection.title} artwork ${index + 1}`}
                      loading="lazy"
                      onError={sanitizeImage}
                    />
                  ))}
                </div>
                {previewCollection.tags && previewCollection.tags.length > 0 && (
                  <ul className="catalog-card__tags">
                    {previewCollection.tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                )}
                <div className="catalog-card__actions">
                  <button
                    type="button"
                    onClick={() => {
                      handleOpenCollection(previewCollection);
                      handleCloseCollectionPreview();
                    }}
                  >
                    View details
                  </button>
                  <button type="button" onClick={() => handleShareCollection(previewCollection)}>
                    Share
                  </button>
                </div>
                {shareFeedback &&
                  shareFeedback.type === 'collection' &&
                  shareFeedback.id === previewCollection.id && (
                    <p className="catalog-card__note" role="status">
                      {shareFeedback.message}
                    </p>
                  )}
              </div>
            </article>
          </div>
        </div>
      )}

      <CollectionComposer
        isOpen={isComposerOpen}
        onCancel={() => {
          setIsComposerOpen(false);
          setEditingCollection(null);
        }}
        onSubmit={handleComposerSubmit}
        availableArtworks={composerArtworks}
        initialCollection={editingCollection}
        mode={editingCollection ? 'edit' : 'create'}
      />
    </section>
  );
};

export default withRouter(Catalog);
